import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import ServiceCard from "@/components/ServiceCard";
import { ShieldCheck, MapPin, ChatCircle, Star, Clock, Trophy, Briefcase } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import TrustScore from "@/components/TrustScore";
import Avatar from "@/components/Avatar";

const tierLabel = { new: "New Seller", verified: "Verified", certified: "Certified Expert" };

export default function SellerProfile() {
  const { user_id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/sellers/${user_id}`).then(({ data }) => setData(data));
  }, [user_id]);

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-20 text-gray-500">Loading…</div>;
  const { profile, user, services, reviews } = data;
  const stats = profile.stats || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden" data-testid="seller-profile">
        <div className="h-24 sm:h-32 bg-gradient-to-r from-mv-primary to-mv-primary-light" />
        <div className="px-5 sm:px-8 pb-6 sm:pb-8 -mt-10 sm:-mt-12">
          <div className="flex items-end gap-4 sm:gap-5 flex-wrap">
            <Avatar src={user?.avatar_url} name={user?.name || ""} size={96} rounded="rounded-2xl" className="border-4 border-white bg-white" testId="seller-profile-avatar" />
            <div className="flex-1 min-w-[200px] pt-2 sm:pt-12">
              <h1 className="font-display font-black text-xl sm:text-2xl text-mv-secondary break-words">{user?.name}</h1>
              <p className="text-gray-600 text-sm sm:text-base">{profile.headline}</p>
              <div className="flex items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500 flex-wrap">
                <span className="inline-flex items-center gap-1 text-mv-primary font-bold">
                  <ShieldCheck size={14} weight="duotone" /> {tierLabel[profile.tier]}
                </span>
                {profile.country && <span className="flex items-center gap-1"><MapPin size={12} /> {profile.country}</span>}
                <span>· {profile.years_experience}y experience</span>
              </div>
            </div>
            <div className="pt-2 sm:pt-12 w-full sm:w-auto">
              <Link to={`/messages?to=${user_id}`} className="block">
                <Button className="bg-mv-secondary hover:bg-mv-primary text-white w-full sm:w-auto" data-testid="contact-seller-btn">
                  <ChatCircle size={16} className="mr-2" weight="duotone" /> Contact
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
            <TrustScore
              score={profile.trust_score}
              label={profile.trust_label}
              isNew={profile.is_new_seller}
              size="lg"
              className="col-span-2 md:col-span-1"
              testId="seller-trust-score"
            />
            {[
              { i: Star, l: "Rating", v: `${(stats.avg_rating || 0).toFixed(2)} (${stats.review_count || 0})` },
              { i: Briefcase, l: "Orders", v: stats.completed_orders || 0 },
              { i: Clock, l: "Response", v: `${stats.response_time_hours || 2}h` },
            ].map(({ i: Icon, l, v }) => (
              <div key={l} className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-xs text-gray-500"><Icon size={14} weight="duotone" /> {l}</div>
                <div className="font-display font-black text-xl text-mv-secondary mt-1">{v}</div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-mv-primary mb-2">About</div>
            <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
          </div>

          {profile.skills?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {profile.skills.map((s) => (
                <span key={s} className="px-3 py-1 rounded-full bg-mv-primary/10 text-mv-primary text-xs font-bold">{s}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      <h2 className="font-display font-bold text-2xl text-mv-secondary mt-10 mb-5">Services ({services.length})</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {services.map((s) => <ServiceCard key={s.id} service={{ ...s, seller: { ...user, tier: profile.tier } }} />)}
      </div>

      {reviews.length > 0 && (
        <>
          <h2 className="font-display font-bold text-2xl text-mv-secondary mt-12 mb-5">Recent reviews</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {reviews.slice(0, 8).map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <Avatar src={r.buyer_avatar} name={r.buyer_name || ""} size={36} testId={`seller-profile-review-avatar-${r.id}`} />
                  <div>
                    <div className="font-bold text-sm">{r.buyer_name}</div>
                    <div className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="ml-auto flex text-amber-500">
                    {Array.from({ length: r.overall }).map((_, i) => <Star key={i} size={14} weight="fill" />)}
                  </div>
                </div>
                <p className="mt-3 text-sm text-gray-700">{r.body}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
